import React from "react";
import {
  Badge,
  Box,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Spinner,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import type { IssueRow } from "#app/lib/data";
import { useIssueView } from "#app/components/issue-view";
import { statusBadgeVariant } from "#app/components/entity/badge-tone";
import { useAgeHistory } from "#app/hooks/issue-view/use-age-history";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { EntityTag } from "#app/components/entity";
import { stripHtmlToText } from "@salestrak/utilities";
import { formatNoteDate } from "#app/lib/utils/format-date";
import type { Omittable } from "@salestrak/ui";

/**
 * Hover popover around any age affordance (the kanban card's age button, a
 * table's age cell). Desktop-only: on touch there is no hover, and wrapping
 * the trigger in a Popover would make a tap both toggle the popover and fire
 * the trigger's own click — so on mobile (or when age is disabled, where
 * there is no history to show) the trigger renders bare and its own handler
 * runs alone.
 */
export function AgeHistoryPopover({
  issue,
  children,
}: Omittable<{
  issue: IssueRow;
  /** The trigger element. Must accept merged props/ref (Base UI `render`). */
  children: React.ReactElement;
}>) {
  const isMobile = useIsMobile();
  const hasAge = !!issue.age?.status && issue.age.status !== "disabled";

  if (isMobile || !hasAge) return children;

  return (
    <Popover>
      <PopoverTrigger openOnHover render={children} />
      {/* The preview is an edge-to-edge list; the stock popover viewport
          insets its content, so the padding is zeroed at the viewport slot. */}
      <PopoverPopup side="top" padding="none" className="w-[300px]">
        <AgeHistoryPreview issue={issue} />
      </PopoverPopup>
    </Popover>
  );
}

/**
 * The popover body. Backed by `useAgeHistory`, which owns its own slim query
 * filtered server-side to `subject: "age"` activities only (newest first).
 * Each row shows the status it was set to, the reason if one was given, and
 * who set it. Clicking a row opens the issue's activity tab and — when that
 * age update carried a comment (so the changeset embeds a `noteId`) — scrolls
 * to it exactly like clicking a note. Bare status changes have no scroll
 * target, so they just open the activity tab. Mounts only when the popover
 * opens, so the query is active for just the one hovered trigger.
 */
function AgeHistoryPreview({ issue }: { issue: IssueRow }) {
  const { openIssue } = useIssueView();
  const { updates, notesById, loading } = useAgeHistory(issue.issueId);

  if (loading && updates.length === 0) {
    return (
      <Box direction="column" padding="md" align="center">
        <Spinner size="md" />
      </Box>
    );
  }

  if (updates.length === 0) {
    return (
      <Box direction="column" padding="md">
        <Text size="sm" variant="muted">
          No age updates yet
        </Text>
      </Box>
    );
  }

  return (
    <Box direction="column">
      {updates.map((update, index) => {
        const after = update.changeset?.age?.after ?? {};
        const status = after.status as AgeStatus | undefined;
        const config = status ? AGE_STATUS_CONFIG[status] : undefined;
        const noteId = after.noteId as string | undefined;
        const noteBody = noteId ? notesById.get(noteId) : undefined;
        const noteText = noteBody ? stripHtmlToText(noteBody) : "";
        return (
          <Box
            as="button"
            key={`${update.activityId}-${index}`}
            direction="column"
            fill="width"
            onClick={() =>
              openIssue(issue, {
                initialTab: "activity",
                focusNoteId: noteId ?? null,
              })
            }
            paddingX="md"
            paddingY="sm"
            gap="xs"
            hoverBackground
          >
            <Box direction="row" align="center" justify="between" gap="sm" fill="width">
              <Box direction="row" align="center" gap="xs" shrink>
                <EntityTag
                  entity={{
                    kind: "user",
                    id: update.author?.userId ?? "system",
                    user: update.author ?? null,
                  }}
                  display="full"
                  size="sm"
                  system={!update.author}
                  interactive={false}
                />
              </Box>
              <Text size="xs" variant="muted" truncate>
                {formatNoteDate(update.creationDate)}
              </Text>
            </Box>
            <Box direction="row" align="center" gap="xs" wrap>
              {config && (
                <Badge size="sm" variant={statusBadgeVariant(config.background)}>
                  <Icon name="clock" size="xs" />
                  {config.label}
                </Badge>
              )}
              {update.reason?.name && (
                <Badge size="sm" variant="outline">
                  {update.reason.name}
                </Badge>
              )}
            </Box>
            {noteText !== "" && (
              <Text size="sm" variant="muted" truncate={2}>
                {noteText}
              </Text>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
